<template>
  <b-modal
    v-model="showSurveyAnswerSelect"
    cancel-variant="outline-secondary"
    centered
    size="xl"
    title="설문 응답 추가"
    no-close-on-esc
    no-close-on-backdrop
    hide-footer
    @close.prevent="close()"
  >
    <form-wizard
      ref="refFormWizard"
      color="#7367F0"
      :title="null"
      :subtitle="null"
      shape="square"
      back-button-text="이전"
      next-button-text="다음"
      finish-button-text="추가"
      class="mb-3"
      nav-class="justify-content-center"
      :start-index="0"
      @on-change="onStepChange"
      @on-complete="onComplete"
    >

      <!-- 1단계: 설문 선택 -->
      <tab-content
        title="설문 선택"
        :before-change="beforeChangeStep"
      >
        <b-row class="mt-25">
          <b-col
            cols="12"
            md="10"
            class="d-flex align-items-center justify-content-start"
          >
            <b-form-input
              v-model="surveyQuery.liveInfoId"
              class="el-def mr-1"
              placeholder="ID"
              style="width:10%"
              @keyup.enter="refetchSurveyList()"
            />
            <v-select
              v-model="surveyQuery.clientId"
              :options="codes.clients"
              :reduce="option => option.code"
              class="el-def mr-1"
              placeholder="제약사"
              style="width:200px;"
            />
            <b-form-input
              v-model="surveyQuery.title"
              class="el-def mr-1"
              placeholder="제목"
              style="width:30%;"
              @keyup.enter="refetchSurveyList()"
            />
            <b-form-datepicker
              v-model="surveyQuery.searchStartDate"
              class="el-def mr-1"
              placeholder="노출기간(Fr)"
              style="width:250px;"
            />
            ~
            <b-form-datepicker
              v-model="surveyQuery.searchEndDate"
              class="el-def ml-1"
              placeholder="노출기간(To)"
              style="width:250px;"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-center justify-content-end"
          >
            <b-button
              variant="primary"
              class="btn-search"
              @click.prevent="refetchSurveyList()"
            >
              조회
            </b-button>
          </b-col>
        </b-row>
        <b-row class="mt-25">
          <span class="ml-25">게시 종료된 설문조사 리스트 [{{ totalRecords }}]건</span>
        </b-row>
        <b-row class="mt-1">
          <b-table
            ref="surveyRefTable"
            primary-key="id"
            :items="fetchSurveyList"
            :fields="surveyTableColumns"
            :sort-by.sync="surveySortBy"
            :sort-desc.sync="surveyIsSortDesc"
            responsive
            sticky-header="460px"
            selectable
            select-mode="single"
            class="position-relative"
            @row-selected="onRowSelectedSurvey"
          >
            <template #cell(period)="data">
              <span class="text-nowrap">
                {{ data.item.startDate | $dateFormatter('YYYY-MM-DD') }} ~ {{ data.item.endDate | $dateFormatter('YYYY-MM-DD') }}
              </span>
            </template>
          </b-table>
        </b-row>
      </tab-content>

      <!-- 2단계: 문항 선택 -->
      <tab-content
        title="문항 선택"
        :before-change="beforeChangeStep"
      >
        <b-card
          v-if="selectedSurvey"
          class="mb-3"
        >
          <strong>{{ `[${selectedSurvey.liveInfoId}] ${selectedSurvey.title}` }}</strong>
        </b-card>
        <b-table
          ref="surveyElementRefTable"
          :items="fetchSurveyElementList"
          :fields="surveyElementTableColumns"
          :sort-by.sync="surveyElementSortBy"
          :sort-desc.sync="surveyElementIsSortDesc"
          responsive
          sticky-header="460px"
          selectable
          select-mode="single"
          class="position-relative"
          @row-selected="onRowSelectedSurveyElement"
        >
          <template #cell(type)="data">
            <span class="text-nowrap">
              {{ getElementTypeLabel(data.item.surveyElementType) }}
            </span>
          </template>
        </b-table>
      </tab-content>

      <!-- 3단계: 응답 선택 -->
      <tab-content
        title="응답 선택"
        :before-change="beforeChangeStep"
      >
        <b-card
          v-if="selectedSurvey && selectedSurveyElement"
          class="mb-3"
        >
          <b-row>
            <b-col>
              <strong>{{ `[${selectedSurvey.liveInfoId}] ${selectedSurvey.title}` }}</strong>
            </b-col>
          </b-row>
          <b-row class="mt-25">
            <b-col>
              <span>{{ `${selectedSurveyElement.surveyPageSeq}p) ${selectedSurveyElement.surveyElementSeq}. ${selectedSurveyElement.surveyElementTitle}` }}</span>
            </b-col>
          </b-row>
        </b-card>
        <b-table
          ref="surveyAnswerRefTable"
          :items="fetchSurveyAnswerList"
          :fields="surveyAnswerTableColumns"
          :sort-by.sync="surveyAnswerSortBy"
          :sort-desc.sync="surveyAnswerIsSortDesc"
          responsive
          sticky-header="460px"
          selectable
          select-mode="multi"
          class="position-relative"
          @row-selected="onRowSelectedSurveyAnswer"
        />
      </tab-content>
    </form-wizard>
  </b-modal>
</template>

<script>
import dayjs from "dayjs"
import axios from '@axios'
import {
  ref, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { errorFormatter, dateFormatter } from '@core/utils/filter'
import vSelect from "vue-select"
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'

export default {
  components: {
    vSelect,
    FormWizard,
    TabContent,
  },

  props: {
    showSurveyAnswerSelect: {
      type: Boolean,
      required: true,
    },
    selectedValue: {
      type: Array,
      default: () => [],
    },
    filterType: {
      type: Number,
      required: true,
    },
  },

  setup(props, { emit }) {
    onMounted(() => {
      fetchCodes()
    })

    const instance = getCurrentInstance()
    const bvModal = instance.proxy.$bvModal
    const toast = useToast()
    const pushToast = (variant, title) => {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }

      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const refFormWizard = ref(null)

    // Query Data
    const surveyQuery = ref({
      liveInfoId: null,
      clientId: null,
      title: null,
      searchStartDate: dayjs().add(-6, 'month').format('YYYY-MM-DD'),
      searchEndDate: dayjs().format('YYYY-MM-DD'),
    })

    const surveyElementQuery = ref({
      surveyPaperId: null,
    })

    const surveyAnswerQuery = ref({
      surveyElementId: null,
    })
    // Query Data End.

    // Set Codes
    const codes = ref({
      clients: [],
      elementType: [],
    })

    const fetchCodes = () => {
      axios.get("/fa/druginfo/codes")
        .then(rs => {
          const {
            clients,
          } = rs.data
          codes.value.clients = clients
        })
        .catch(() => {
          pushToast('danger', '코드 데이터를 불러오는데 실패하였습니다.')
        })

      axios.get("/fa/survey-paper/codes")
        .then(rs => {
          const {
            surveyElementType,
            surveyAutoType,
          } = rs.data
          codes.value.elementType = surveyElementType.filter(el => el.code !== 100)
          codes.value.autoType = surveyAutoType
        })
        .catch(() => {
          pushToast('danger', '코드 데이터를 불러오는데 실패하였습니다.')
        })
    }
    // Set Codes End.

    // 현재 단계 및 상태 관리
    const currentStepIndex = ref(0)
    const selectedSurvey = ref(null)
    const selectedSurveyElement = ref(null)
    const selectedAnswer = ref([])

    const resetWizardSteps = nextIndex => {
      const wizard = refFormWizard.value
      if (wizard && wizard.tabs) {
        wizard.tabs.forEach((tab, index) => {
          if (index > nextIndex) {
            if (tab.$data) {
              tab.$data.checked = false
            }
          }
        })
      }
    }

    const resetSelectedValues = (stepIndex, direction) => {
      if (direction === 'forward') {
      // 다음 단계로 이동할 때
        if (stepIndex === 0) {
          selectedSurveyElement.value = null
          selectedAnswer.value = []
        }
        if (stepIndex === 1) {
          selectedAnswer.value = []
        }
      } else if (direction === 'backward') {
      // 이전 단계로 이동할 때
        if (stepIndex < 2) {
          selectedAnswer.value = []
        }
        if (stepIndex < 1) {
          selectedSurveyElement.value = null
        }
        if (stepIndex < 0) {
          selectedSurvey.value = null
        }
      }
    }

    // 단계 변경 이벤트
    const onStepChange = (prevIndex, nextIndex) => {
      if (!refFormWizard.value) return false
      if (nextIndex < 0) return false
      if (nextIndex < prevIndex) {
        resetWizardSteps(nextIndex)
        resetSelectedValues(nextIndex, 'backward')
      }
      currentStepIndex.value = nextIndex

      if (prevIndex === 0 && nextIndex === 1) {
        refetchSurveyElementList()
      }
      if (prevIndex === 1 && nextIndex === 2) {
        refetchSurveyAnswerList()
      }
      return true
    }

    // 단계별 유효성 검사
    const beforeChangeStep = () => {
      if (currentStepIndex.value === 0 && !selectedSurvey.value) {
        pushToast('warning', '설문을 선택하세요')
        return false
      }
      if (currentStepIndex.value === 1 && !selectedSurveyElement.value) {
        pushToast('warning', '문항을 선택하세요')
        return false
      }
      if (currentStepIndex.value === 2 && (!selectedAnswer.value || selectedAnswer.value.length === 0)) {
        pushToast('warning', '응답을 선택하세요')
        return false
      }
      return true
    }

    // 설문 선택 이벤트(1단계)
    const onRowSelectedSurvey = items => {
      if (items && items.length > 0) {
        resetSelectedValues(0, 'forward')
        // eslint-disable-next-line prefer-destructuring
        selectedSurvey.value = items[0]
        surveyElementQuery.value.surveyPaperId = items[0].surveyPaperId
      } else {
        selectedSurvey.value = null
      }
    }

    // 문항 선택 이벤트(2단계)
    const onRowSelectedSurveyElement = items => {
      if (items && items.length > 0) {
        resetSelectedValues(1, 'forward')
        // eslint-disable-next-line prefer-destructuring
        selectedSurveyElement.value = items[0]
        surveyAnswerQuery.value.surveyElementId = items[0].surveyElementId
      } else {
        selectedSurveyElement.value = null
      }
    }

    // 응답 선택 이벤트(3단계)
    const onRowSelectedSurveyAnswer = items => {
      selectedAnswer.value = items
    }

    // 추가 버튼 이벤트
    const onComplete = () => {
      // if (!selectedAnswer.value || selectedAnswer.value.length === 0) {
      //   pushToast('warning', '설문 응답을 선택하세요')
      //   return
      // }

      const newCount = selectedAnswer.value.filter(
        item => !props.selectedValue.includes(item.answerId),
      ).length

      if (props.selectedValue.length + newCount > 5) {
        bvModal.msgBoxOk('설문 응답은 최대 5개까지 추가할 수 있습니다.', {
          okTitle: '확인',
        })
        return
      }

      const selectedSurveyAnswerList = []
      selectedAnswer.value.forEach(item => {
        selectedSurveyAnswerList.push({
          liveInfoId: selectedSurvey.value.liveInfoId,
          filterId: item.answerId,
          filterType: props.filterType,
          clientName: selectedSurvey.value.clientName,
          title: selectedSurvey.value.title,
          elementTitle: selectedSurveyElement.value.surveyElementTitle,
          answerDescription: item.answerDescription,
          period: `${dateFormatter(selectedSurvey.value.startDate, 'YYYY-MM-DD')} ~ ${dateFormatter(selectedSurvey.value.endDate, 'YYYY-MM-DD')}`,
        })
      })
      resetWizard()
      emit('close', selectedSurveyAnswerList)
    }

    const close = () => {
      resetWizard()
      emit('close', null)
    }

    const resetWizard = () => {
      if (refFormWizard.value) {
        refFormWizard.value.reset()
      }

      currentStepIndex.value = 0
      selectedSurvey.value = null
      selectedSurveyElement.value = null
      selectedAnswer.value = []

      surveyQuery.value = {
        liveInfoId: null,
        clientId: null,
        title: null,
        searchStartDate: dayjs().add(-6, 'month').format('YYYY-MM-DD'),
        searchEndDate: dayjs().format('YYYY-MM-DD'),
      }
    }

    // Element Type 라벨 가져오기
    const getElementTypeLabel = typeCode => {
      const elementType = codes.value.elementType.find(el => el.code === typeCode)
      return elementType ? elementType.label : typeCode
    }

    // 테이블 관련
    const surveyRefTable = ref(null)
    const surveySortBy = ref('liveInfoId')
    const surveyIsSortDesc = ref(true)
    const surveyElementRefTable = ref(null)
    const surveyElementSortBy = ref('surveyPageSeq')
    const surveyElementIsSortDesc = ref(false)
    const surveyAnswerRefTable = ref(null)
    const surveyAnswerSortBy = ref('answerSeq')
    const surveyAnswerIsSortDesc = ref(false)
    const totalRecords = ref(0)

    const surveyTableColumns = [
      {
        key: 'liveInfoId', label: 'Id', sortable: true, thStyle: { width: '10%' },
      },
      {
        key: 'clientName', label: '제약사명', sortable: true, thStyle: { width: '20%' },
      },
      { key: 'title', label: '제목', sortable: true },
      { key: 'period', label: '게시 기간', sortable: true },
    ]
    const surveyElementTableColumns = [
      {
        key: 'surveyPageSeq', label: '페이지', sortable: true, thStyle: { width: '10%' },
      },
      {
        key: 'surveyElementSeq', label: '번호', sortable: true, thStyle: { width: '10%' },
      },
      {
        key: 'surveyElementTitle', label: '문항', sortable: true, thStyle: { width: '50%' },
      },
      {
        key: 'type', label: '타입', sortable: true, thStyle: { width: '30%' },
      },
    ]
    const surveyAnswerTableColumns = [
      {
        key: 'answerSeq', label: '번호', sortable: true, thStyle: { width: '10%' },
      },
      {
        key: 'answerDescription', label: '응답', sortable: true, thStyle: { width: '90%' },
      },
    ]

    const surveySortFieldMap = {
      period: 'startDate',
    }
    const surveyElementSortFieldMap = {
      type: 'surveyElementType',
    }

    const refetchSurveyList = () => {
      surveyRefTable.value.refresh()
    }

    const fetchSurveyList = (ctx, callback) => {
      if (!surveyQuery.value.searchStartDate || !surveyQuery.value.searchEndDate) {
        pushToast('warning', '조회 기간을 설정하세요')
        callback([])
        totalRecords.value = 0
        return
      }

      axios.post("/fa/survey-answer-select/survey/list", {
        search: {
          query: surveyQuery.value,
          surveyStatus: 9, // 게시종료
        },
        sort: {
          predicate: surveySortFieldMap[surveySortBy.value] || surveySortBy.value,
          reverse: surveyIsSortDesc.value,
        },
      })
        .then(rs => {
          const { items, totalRecord } = rs.data
          callback(items)
          totalRecords.value = totalRecord
        })
        .catch(error => {
          pushToast('danger', errorFormatter(error, '게시 종료된 설문조사 목록 조회에 실패하였습니다.'))
        })
    }

    const refetchSurveyElementList = () => {
      surveyElementRefTable.value.refresh()
    }
    const fetchSurveyElementList = (ctx, callback) => {
      if (!selectedSurvey || !selectedSurvey.value.surveyPaperId) {
        pushToast('warning', '설문조사를 설정하세요')
        callback([])
        return
      }

      axios.post("/fa/survey-answer-select/survey-element/list", {
        search: {
          query: surveyElementQuery.value,
        },
        sort: {
          predicate: surveyElementSortFieldMap[surveyElementSortBy.value] || surveyElementSortBy.value,
          reverse: surveyElementIsSortDesc.value,
        },
      })
        .then(rs => {
          const { items } = rs.data
          callback(items)
        })
        .catch(error => {
          pushToast('danger', errorFormatter(error, '문항 목록 조회에 실패하였습니다.'))
        })
    }

    const refetchSurveyAnswerList = () => {
      surveyAnswerRefTable.value.refresh()
    }
    const fetchSurveyAnswerList = (ctx, callback) => {
      if (!selectedSurveyElement || !selectedSurveyElement.value.surveyElementId) {
        pushToast('warning', '문항을 설정하세요')
        callback([])
        return
      }

      axios.post("/fa/survey-answer-select/survey-answer/list", {
        search: {
          query: surveyAnswerQuery.value,
        },
        sort: {
          predicate: surveyAnswerSortBy.value,
          reverse: surveyAnswerIsSortDesc.value,
        },
      })
        .then(rs => {
          const { items } = rs.data
          callback(items)
        })
        .catch(error => {
          pushToast('danger', errorFormatter(error, '응답 목록 조회에 실패하였습니다.'))
        })
    }

    return {
      codes,
      refFormWizard,
      currentStepIndex,
      selectedSurvey,
      selectedSurveyElement,
      selectedAnswer,
      onStepChange,
      onComplete,
      beforeChangeStep,
      onRowSelectedSurvey,
      onRowSelectedSurveyElement,
      onRowSelectedSurveyAnswer,
      getElementTypeLabel,
      surveyQuery,
      surveyElementQuery,
      surveyAnswerQuery,
      surveyRefTable,
      surveyElementRefTable,
      surveyAnswerRefTable,
      surveySortBy,
      surveyIsSortDesc,
      surveyElementSortBy,
      surveyElementIsSortDesc,
      surveyAnswerSortBy,
      surveyAnswerIsSortDesc,
      totalRecords,
      surveyTableColumns,
      surveyElementTableColumns,
      surveyAnswerTableColumns,
      refetchSurveyList,
      fetchSurveyList,
      refetchSurveyElementList,
      fetchSurveyElementList,
      refetchSurveyAnswerList,
      fetchSurveyAnswerList,
      close,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';

.vue-form-wizard {
  // 탭들 가운데 정렬
  .wizard-nav.wizard-nav-pills {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;

    li {
      margin: 0 15px; // 탭들 사이 간격
    }
  }
}
</style>
